import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Modal,
  TextField,
  Select,
  Text,
  BlockStack,
  Banner,
  Spinner,
  Tabs,
  DataTable,
  Grid,
  Toast,
  Checkbox,
  Button
} from '@shopify/polaris';

import axios from 'axios';
import { API_CONFIG } from '../config/api';

interface PromptCategory {
  id: number;
  name: string;
  description?: string;
  is_default: boolean;
  prompt_models_count?: number;
  [key: string]: unknown;
}

interface PromptModel {
  id: number;
  name: string;
  prompt: string;
  response_format?: string;
  is_default: boolean;
  prompt_type?: 'transcription' | 'translation' | 'custom';
  system_fixed?: boolean;
  active?: boolean;
  category_id?: number;
  category_name?: string;
  usage_count?: number;
  total_credits_used?: number;
  average_credits_per_request?: number;
  last_used_at?: string;
  usage_by_feature_type?: Record<string, number>;
  [key: string]: unknown;
}

interface AiRequestLog {
  id: number;
  model: string;
  question: string;
  response: string;
  feature_type: string;
  credits_used: number;
  vendor: string;
  created_at: string;
  user_name?: string;
  prompt_model_name?: string;
  [key: string]: unknown;
}

const AiPromptManagement: React.FC = () => {
  const [categories, setCategories] = useState<PromptCategory[]>([]);
  const [promptModels, setPromptModels] = useState<PromptModel[]>([]);
  const [aiRequestLogs, setAiRequestLogs] = useState<AiRequestLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState<number | null>(null);
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showModelModal, setShowModelModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState<any>(null);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form states
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: '',
    is_default: false,
  });

  const [modelForm, setModelForm] = useState({
    name: '',
    prompt: '',
    response_format: '',
    prompt_category_id: '',
    prompt_type: '',
    is_default: false,
    active: true,
  });

  // Helper: pick a default category for new prompts
  const getDefaultCategoryId = (): string => {
    const defaultCategory = categories.find(c => c.is_default) || categories[0];
    return defaultCategory ? defaultCategory.id.toString() : '';
  };

  // Toast state
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const showToast = (message: string) => {
    setToastMessage(message);
    setToastActive(true);
    setTimeout(() => setToastActive(false), 3000);
  };

  // Load data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesRes, modelsRes, logsRes] = await Promise.all([
        axios.get(`${API_CONFIG.BASE_URL}/api/v1/ai_settings/prompt_categories`),
        axios.get(`${API_CONFIG.BASE_URL}/api/v1/ai_settings/prompt_models`),
        axios.get(`${API_CONFIG.BASE_URL}/api/v1/ai_request_logs`)
      ]);

      if (categoriesRes.data.success) {
        setCategories(categoriesRes.data.data.prompt_categories);
      }
      if (modelsRes.data.success) {
        setPromptModels(modelsRes.data.data.prompt_models);
      }
      if (logsRes.data.success) {
        setAiRequestLogs(logsRes.data.data.ai_request_logs);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const createCategory = async () => {
    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/api/v1/ai_settings/create_prompt_category`, {
        prompt_category: categoryForm
      });

      if (response.data.success) {
        showToast('Category created successfully!');
        setShowCategoryModal(false);
        setCategoryForm({ name: '', description: '', is_default: false });
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create category');
    }
  };

  // Validate prompt name uniqueness within category
  const validatePromptName = (name: string, categoryId: string, excludeId?: number): boolean => {
    if (!name.trim() || !categoryId) return true; // Skip validation if name or category is empty
    
    const existingPrompt = promptModels.find(model => 
      model.name.toLowerCase() === name.toLowerCase() && 
      model.category_id === parseInt(categoryId) &&
      (!excludeId || model.id !== excludeId)
    );
    
    return !existingPrompt;
  };

  const createModel = async () => {
    // Require category selection
    if (!modelForm.prompt_category_id) {
      setError('Please select a category');
      return;
    }

    // Validate name uniqueness
    if (!validatePromptName(modelForm.name, modelForm.prompt_category_id)) {
      setError('A prompt with this name already exists in the selected category');
      return;
    }

    try {
      const response = await axios.post(`${API_CONFIG.BASE_URL}/api/v1/ai_settings/create_prompt_model`, {
        prompt_model: modelForm
      });

      if (response.data.success) {
        showToast('Prompt created successfully!');
        setShowModelModal(false);
        setModelForm({ name: '', prompt: '', response_format: '', prompt_category_id: '', prompt_type: '', is_default: false, active: true });
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create prompt');
    }
  };

  const updateCategory = async (id: number) => {
    try {
      const response = await axios.patch(`${API_CONFIG.BASE_URL}/api/v1/ai_settings/prompt_categories/${id}`, {
        prompt_category: categoryForm
      });

      if (response.data.success) {
        showToast('Category updated successfully!');
        setShowCategoryModal(false);
        setCategoryForm({ name: '', description: '', is_default: false });
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update category');
    }
  };

  const updateModel = async (id: number) => {
    // Validate name uniqueness (exclude current prompt from check)
    if (!validatePromptName(modelForm.name, modelForm.prompt_category_id, id)) {
      setError('A prompt with this name already exists in the selected category');
      return;
    }

    try {
      const response = await axios.patch(`${API_CONFIG.BASE_URL}/api/v1/ai_settings/prompt_models/${id}`, {
        prompt_model: modelForm
      });

      if (response.data.success) {
        showToast('Prompt updated successfully!');
        setShowModelModal(false);
        setModelForm({ name: '', prompt: '', response_format: '', prompt_category_id: '', prompt_type: '', is_default: false, active: true });
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update prompt');
    }
  };

  const deleteCategory = async (id: number) => {
    try {
      const response = await axios.delete(`${API_CONFIG.BASE_URL}/api/v1/ai_settings/prompt_categories/${id}`);

      if (response.data.success) {
        showToast('Category deleted successfully!');
        setShowDeleteModal(false);
        setDeleteItem(null);
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete category');
    }
  };

  const deleteModel = async (id: number) => {
    try {
      const response = await axios.delete(`${API_CONFIG.BASE_URL}/api/v1/ai_settings/prompt_models/${id}`);

      if (response.data.success) {
        showToast('Prompt deleted successfully!');
        setShowDeleteModal(false);
        setDeleteItem(null);
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete prompt');
    }
  };

  const togglePromptStatus = async (id: number) => {
    try {
      const response = await axios.patch(`${API_CONFIG.BASE_URL}/api/v1/ai_settings/prompt_models/${id}/toggle_status`);

      if (response.data.success) {
        showToast(response.data.message || 'Prompt status updated successfully!');
        loadData();
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to toggle prompt status');
    }
  };

  const openEditCategoryModal = (category: PromptCategory) => {
    setEditingItem(category);
    setIsEditing(true);
    setCategoryForm({
      name: category.name,
      description: category.description || '',
      is_default: category.is_default
    });
    setShowCategoryModal(true);
  };

  const openEditModelModal = (model: PromptModel) => {
    setEditingItem(model);
    setIsEditing(true);
    setModelForm({
      name: model.name,
      prompt: model.prompt,
      response_format: model.response_format || '',
      prompt_category_id: model.category_id?.toString() || '',
      prompt_type: model.prompt_type || '',
      is_default: model.is_default,
      active: model.active ?? true
    });
    setShowModelModal(true);
  };

  const openCreateModelModal = () => {
    setEditingItem(null);
    setIsEditing(false);
    setModelForm({
      name: '',
      prompt: '',
      response_format: '',
      prompt_category_id: getDefaultCategoryId(),
      prompt_type: 'custom',
      is_default: false,
      active: true,
    });
    setShowModelModal(true);
  };

  const openDeleteModal = (item: any, type: 'category' | 'model') => {
    setDeleteItem({ ...item, type });
    setShowDeleteModal(true);
  };

  const handleCategorySubmit = () => {
    if (isEditing && editingItem) {
      updateCategory(editingItem.id);
    } else {
      createCategory();
    }
  };

  const handleModelSubmit = () => {
    if (isEditing && editingItem) {
      updateModel(editingItem.id);
    } else {
      createModel();
    }
  };

  const handleDelete = () => {
    if (deleteItem?.type === 'category') {
      deleteCategory(deleteItem.id);
    } else if (deleteItem?.type === 'model') {
      deleteModel(deleteItem.id);
    }
  };

  const resetModals = () => {
    setShowCategoryModal(false);
    setShowModelModal(false);
    setShowDeleteModal(false);
    setEditingItem(null);
    setIsEditing(false);
    setDeleteItem(null);
    setCategoryForm({ name: '', description: '', is_default: false });
    setModelForm({ name: '', prompt: '', response_format: '', prompt_category_id: '', prompt_type: '', is_default: false, active: true });
  };

  const handleViewCategoryPrompts = (categoryId: number) => {
    setSelectedCategoryFilter(categoryId);
    setSelectedTab(1); // Switch to prompts tab
  };

  const clearCategoryFilter = () => {
    setSelectedCategoryFilter(null);
  };

  // Category options for select
  const categoryOptions = categories.map(category => ({
    label: category.name,
    value: category.id.toString()
  }));

  // Prompt type options
  const promptTypeOptions = [
    { label: 'Custom', value: 'custom' }
  ];

  // Tabs configuration
  const tabs = [
    { id: 'categories', content: 'Categories' },
    { id: 'models', content: 'Prompts' },
    { id: 'usage', content: 'Usage & Analytics' },
    { id: 'logs', content: 'AI Request Logs' }
  ];

  // Category table rows with actions
  const categoryRows = categories.map((category) => [
    category.name,
    category.description || 'No description',
    category.is_default ? 'Default' : 'Custom',
    `${category.prompt_models_count || 0} prompts`,
    <div key={`actions-${category.id}`} style={{ display: 'flex', gap: '8px' }}>
      <Button
        size="slim"
        variant="plain"
        onClick={() => handleViewCategoryPrompts(category.id)}
      >
        View Prompts
      </Button>
      <Button
        size="slim"
        variant="plain"
        onClick={() => openEditCategoryModal(category)}
      >
        Edit
      </Button>
      <Button
        size="slim"
        variant="plain"
        tone="critical"
        onClick={() => openDeleteModal(category, 'category')}
      >
        Delete
      </Button>
    </div>
  ]);

  // Filter prompts based on selected category
  const filteredPromptModels = selectedCategoryFilter 
    ? promptModels.filter(model => model.category_id === selectedCategoryFilter)
    : promptModels;

  // Model table rows with actions
  const modelRows = filteredPromptModels.map((model) => [
    <div key={`name-${model.id}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {model.name}
      {model.system_fixed && (
        <span style={{ 
          fontSize: '10px', 
          backgroundColor: '#f0f0f0', 
          color: '#666', 
          padding: '2px 6px', 
          borderRadius: '3px',
          fontWeight: 'bold'
        }}>
          SYSTEM
        </span>
      )}
    </div>,
    model.category_name || 'Uncategorized',
    model.prompt_type ? model.prompt_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Custom',
    model.system_fixed ? 'Yes' : 'No',
    model.is_default ? 'Default' : 'Custom',
    <div key={`status-${model.id}`} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <span style={{ 
        color: model.active ? '#008060' : '#d72c0d',
        fontWeight: 'bold'
      }}>
        {model.active ? 'Active' : 'Inactive'}
      </span>
      {!model.system_fixed && (
        <Button
          size="slim"
          variant="plain"
          onClick={() => togglePromptStatus(model.id)}
          tone={model.active ? 'critical' : 'success'}
        >
          {model.active ? 'Disable' : 'Enable'}
        </Button>
      )}
    </div>,
    `${model.usage_count || 0} uses`,
    `${model.total_credits_used || 0} credits`,
    <div key={`actions-${model.id}`} style={{ display: 'flex', gap: '8px' }}>
      <Button
        size="slim"
        variant="plain"
        onClick={() => openEditModelModal(model)}
      >
        Edit
      </Button>
      <Button
        size="slim"
        variant="plain"
        tone="critical"
        onClick={() => openDeleteModal(model, 'model')}
        disabled={model.system_fixed}
      >
        Delete
      </Button>
    </div>
  ]);

  // AI Request Logs table rows
  const logRows = aiRequestLogs.map((log) => [
    log.feature_type?.replace(/_/g, ' ').toUpperCase() || 'Unknown',
    log.model,
    `${log.credits_used} credits`,
    log.vendor,
    new Date(log.created_at).toLocaleDateString(),
    log.prompt_model_name || 'Default'
  ]);

  // Usage statistics
  const usageStats = {
    totalRequests: aiRequestLogs.length,
    totalCredits: aiRequestLogs.reduce((sum, log) => sum + (log.credits_used || 0), 0),
    avgCreditsPerRequest: aiRequestLogs.length > 0 ? 
      aiRequestLogs.reduce((sum, log) => sum + (log.credits_used || 0), 0) / aiRequestLogs.length : 0
  };

  if (loading) {
    return (
      <Page title="AI Prompt Management" fullWidth>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p">Loading AI prompt management...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page 
      title="AI Prompt Management" 
      subtitle="Manage prompt templates and view usage analytics"
      fullWidth
    >
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            <Text as="p">{error}</Text>
          </Banner>
        )}

        <Layout>
          <Layout.Section>
            <Card>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Tabs
                    tabs={tabs}
                    selected={selectedTab}
                    onSelect={setSelectedTab}
                  />
                  <div>
                    {selectedTab === 0 && (
                      <Button
                        variant="primary"
                        onClick={() => setShowCategoryModal(true)}
                      >
                        Add Category
                      </Button>
                    )}
                    {selectedTab === 1 && (
                      <Button
                        variant="primary"
                        onClick={openCreateModelModal}
                      >
                        Add Prompt
                      </Button>
                    )}
                  </div>
                </div>
              </div>
              
              <div>
                {selectedTab === 0 && (
                  <BlockStack gap="400">
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'text', 'text']}
                      headings={['Name', 'Description', 'Type', 'Prompts', 'Actions']}
                      rows={categoryRows}
                    />
                  </BlockStack>
                )}

                {selectedTab === 1 && (
                  <BlockStack gap="400">
                    {selectedCategoryFilter && (
                      <Banner tone="info" onDismiss={clearCategoryFilter}>
                        <Text as="p">
                          Showing prompts for category: <strong>{categories.find(c => c.id === selectedCategoryFilter)?.name}</strong>
                          {' '}({filteredPromptModels.length} of {promptModels.length} prompts)
                        </Text>
                      </Banner>
                    )}
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text', 'text', 'text']}
                      headings={['Name', 'Category', 'Prompt Type', 'System Fixed', 'Default', 'Status', 'Usage', 'Credits', 'Actions']}
                      rows={modelRows}
                    />
                  </BlockStack>
                )}

                {selectedTab === 2 && (
                  <BlockStack gap="400">
                    <Grid>
                      <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 4 }}>
                        <Card padding="400">
                          <BlockStack gap="300" align="center">
                            <Text variant="headingLg" as="h2" tone="success">
                              {usageStats.totalRequests}
                            </Text>
                            <Text variant="bodyMd" fontWeight="semibold" as="p">
                              Total Requests
                            </Text>
                          </BlockStack>
                        </Card>
                      </Grid.Cell>
                      
                      <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 4 }}>
                        <Card padding="400">
                          <BlockStack gap="300" align="center">
                            <Text variant="headingLg" as="h2" tone="success">
                              {usageStats.totalCredits.toFixed(1)}
                            </Text>
                            <Text variant="bodyMd" fontWeight="semibold" as="p">
                              Total Credits Used
                            </Text>
                          </BlockStack>
                        </Card>
                      </Grid.Cell>
                      
                      <Grid.Cell columnSpan={{ xs: 6, sm: 6, md: 4, lg: 4, xl: 4 }}>
                        <Card padding="400">
                          <BlockStack gap="300" align="center">
                            <Text variant="headingLg" as="h2" tone="success">
                              {usageStats.avgCreditsPerRequest.toFixed(2)}
                            </Text>
                            <Text variant="bodyMd" fontWeight="semibold" as="p">
                              Avg Credits/Request
                            </Text>
                          </BlockStack>
                        </Card>
                      </Grid.Cell>
                    </Grid>
                  </BlockStack>
                )}

                {selectedTab === 3 && (
                  <BlockStack gap="400">
                    <DataTable
                      columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text']}
                      headings={['Feature', 'Prompt', 'Credits', 'Vendor', 'Date', 'Prompt Model']}
                      rows={logRows}
                    />
                  </BlockStack>
                )}
              </div>
            </Card>
          </Layout.Section>
        </Layout>

        {/* Category Modal */}
        <Modal
          open={showCategoryModal}
          onClose={resetModals}
          title={isEditing ? "Edit Category" : "Add Category"}
          primaryAction={{
            content: isEditing ? 'Update' : 'Create',
            onAction: handleCategorySubmit,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: resetModals,
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <TextField
                label="Name"
                value={categoryForm.name}
                onChange={(value) => setCategoryForm(prev => ({ ...prev, name: value }))}
                autoComplete="off"
              />
              <TextField
                label="Description"
                value={categoryForm.description}
                onChange={(value) => setCategoryForm(prev => ({ ...prev, description: value }))}
                multiline={3}
                autoComplete="off"
              />
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Model Modal */}
        <Modal
          open={showModelModal}
          onClose={resetModals}
          title={isEditing ? "Edit Prompt" : "Add Prompt"}
          primaryAction={{
            content: isEditing ? 'Update' : 'Create',
            onAction: handleModelSubmit,
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: resetModals,
            },
          ]}
        >
          <Modal.Section>
            <BlockStack gap="400">
              <TextField
                label="Name"
                value={modelForm.name}
                onChange={(value) => setModelForm(prev => ({ ...prev, name: value }))}
                autoComplete="off"
                error={
                  modelForm.name && modelForm.prompt_category_id && 
                  !validatePromptName(modelForm.name, modelForm.prompt_category_id, isEditing ? (editingItem as PromptModel)?.id : undefined)
                    ? 'A prompt with this name already exists in the selected category'
                    : undefined
                }
              />
              {!isEditing ? (
                <Select
                  label="Category"
                  options={categoryOptions}
                  value={modelForm.prompt_category_id}
                  onChange={(value) => setModelForm(prev => ({ ...prev, prompt_category_id: value }))}
                />
              ) : (
                <TextField
                  label="Category"
                  value={categories.find(c => c.id.toString() === modelForm.prompt_category_id)?.name || 'Unknown'}
                  disabled={true}
                  helpText="Category cannot be changed after creation. Delete and recreate if needed."
                  autoComplete="off"
                />
              )}
              {!isEditing ? (
                <Select
                  label="Prompt Type"
                  options={promptTypeOptions}
                  value={modelForm.prompt_type}
                  onChange={(value) => setModelForm(prev => ({ ...prev, prompt_type: value }))}
                  helpText="Select the type of prompt this model handles"
                />
              ) : (
                <TextField
                  label="Prompt Type"
                  value={modelForm.prompt_type}
                  disabled={true}
                  helpText="Prompt type cannot be changed after creation. Delete and recreate if needed."
                  autoComplete="off"
                />
              )}
              <TextField
                label="Prompt Template"
                value={modelForm.prompt}
                onChange={(value) => setModelForm(prev => ({ ...prev, prompt: value }))}
                multiline={5}
                autoComplete="off"
                helpText="Use placeholders like {transcript}, {summary}, etc."
              />
              <TextField
                label="Response Format (JSON Schema)"
                value={modelForm.response_format}
                onChange={(value) => setModelForm(prev => ({ ...prev, response_format: value }))}
                multiline={8}
                autoComplete="off"
                helpText="Optional JSON schema to define the expected response format. Leave empty for free-form responses."
                placeholder='{"type": "object", "properties": {"result": {"type": "string"}}, "required": ["result"]}'
              />
              <Checkbox
                label="Active"
                checked={modelForm.active}
                onChange={(checked) => setModelForm(prev => ({ ...prev, active: checked }))}
                helpText="Inactive prompts will not be used during AI analysis"
              />
            </BlockStack>
          </Modal.Section>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          title="Confirm Delete"
          primaryAction={{
            content: 'Delete',
            onAction: handleDelete,
            destructive: true,
            disabled: deleteItem?.system_fixed
          }}
          secondaryActions={[
            {
              content: 'Cancel',
              onAction: () => setShowDeleteModal(false),
            },
          ]}
        >
          <Modal.Section>
            {deleteItem?.system_fixed ? (
              <Banner tone="warning">
                <Text as="p">
                  This is a system-fixed prompt and cannot be deleted. System prompts are required for core functionality.
                </Text>
              </Banner>
            ) : (
              <>
                <Text as="p">
                  Are you sure you want to delete this {deleteItem?.type}? This action cannot be undone.
                </Text>
                {deleteItem && (
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    {deleteItem.name}
                  </Text>
                )}
              </>
            )}
          </Modal.Section>
        </Modal>

        {toastActive && (
          <Toast
            content={toastMessage}
            onDismiss={() => setToastActive(false)}
            duration={3000}
          />
        )}
      </Page>
  );
};

export default AiPromptManagement;
